/**
 * @fileoverview Own the inspector/editor for resource grants, public links, access requests, and sharing history. Backend capability decisions govern reads and accepted mutations; local tab/form state is presentation. `kc-internal-backend/functions/document_access.py` owns internal ACLs and public-link admission. Requests, comments, grants, and link credentials remain distinct access records rather than interchangeable signs of permission.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import api from '../../api'
import './DocumentSharePanel.css'

const ROLES = ['viewer', 'commenter', 'editor', 'manager']



function message(error, fallback) {
  return error?.response?.data?.error || error?.message || fallback
}



function formatDate(timestamp) {
  if (!timestamp) return ''
  return new Date(Number(timestamp) * 1000).toLocaleString()
}



function resourceUrl(resourceType, resourceId, suffix = '') {
  return `/simul/archive/access/${encodeURIComponent(resourceType)}/${encodeURIComponent(resourceId)}${suffix}`
}



function roleLabel(role) {
  return String(role || '').replace(/^./, value => value.toUpperCase())
}



export default function DocumentSharePanel({
  resourceType = 'document',
  resourceId,
  resourceName = 'Document',
  directVersionUrl = '',
  hideComments = false,
  onClose,
}) {
  const [tab, setTab] = useState('people')
  const [access, setAccess] = useState(null)
  const [links, setLinks] = useState([])
  const [comments, setComments] = useState([])
  const [requests, setRequests] = useState([])
  const [events, setEvents] = useState([])
  const [principalId, setPrincipalId] = useState('')
  const [principalType, setPrincipalType] = useState('user')
  const [grantRole, setGrantRole] = useState('viewer')
  const [linkRole, setLinkRole] = useState('viewer')
  const [allowDownload, setAllowDownload] = useState(false)
  const [expiresAt, setExpiresAt] = useState('')
  const [password, setPassword] = useState('')
  const [newLink, setNewLink] = useState(null)
  const [commentBody, setCommentBody] = useState('')
  const [replyTo, setReplyTo] = useState(null)
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState('')
  const [error, setError] = useState('')
  const panelRef = useRef(null)
  const tabRefs = useRef(new Map())
  const loadSequenceRef = useRef(0)

  const capabilities = useMemo(() => new Set(access?.decision?.capabilities || []), [access])
  // A legacy archive item has no ACL yet. Any authenticated editor may claim it
  // and become its initial owner; configured items require manager capability.
  const canManage = capabilities.has('manage_access') || (!access?.access_control && access?.decision?.role === 'editor')
  const canComment = capabilities.has('comment')

  const load = useCallback(

  async () => {
    if (!resourceId) return
    const sequence = loadSequenceRef.current + 1
    loadSequenceRef.current = sequence
    setError('')
    try {
      const accessResponse = await api.get(resourceUrl(resourceType, resourceId))
      if (sequence !== loadSequenceRef.current) return
      const value = accessResponse.data || {}
      setAccess(value)
      const caps = new Set(value?.decision?.capabilities || [])
      const tasks = []
      if (caps.has('comment') && !hideComments) {
        tasks.push(api.get(resourceUrl(resourceType, resourceId, '/comments')).then(response => {
          if (sequence === loadSequenceRef.current) setComments(response.data?.comments || [])
        }))
      }
      if (caps.has('manage_access')) {
        tasks.push(api.get(resourceUrl(resourceType, resourceId, '/share-links')).then(response => {
          if (sequence === loadSequenceRef.current) setLinks(response.data?.share_links || [])
        }))
        tasks.push(api.get(resourceUrl(resourceType, resourceId, '/requests')).then(response => {
          if (sequence === loadSequenceRef.current) setRequests(response.data?.access_requests || [])
        }))
        tasks.push(api.get(resourceUrl(resourceType, resourceId, '/history')).then(response => {
          if (sequence === loadSequenceRef.current) setEvents(response.data?.events || [])
        }))
      }
      await Promise.all(tasks)
    } catch (loadError) {
      if (sequence === loadSequenceRef.current) setError(message(loadError, 'Sharing details could not be loaded'))
    }
  }, [resourceId, resourceType, hideComments])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    panelRef.current?.focus()
    return () => { loadSequenceRef.current += 1 }
  }, [])

  const tabs = useMemo(() => [
    ['people', 'People'], ['links', 'Links'],
    ...(!hideComments ? [['comments', `Comments${comments.length ? ` (${comments.length})` : ''}`]] : []),
    ...(canManage ? [['activity', 'Activity']] : []),
  ], [canManage, comments.length, hideComments])

  const selectAdjacentTab =

  event => {
    const currentIndex = tabs.findIndex(([value]) => value === tab)
    let nextIndex = currentIndex
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length
    else if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length
    else if (event.key === 'Home') nextIndex = 0
    else if (event.key === 'End') nextIndex = tabs.length - 1
    else return
    event.preventDefault()
    const nextTab = tabs[nextIndex][0]
    setTab(nextTab)
    window.requestAnimationFrame(() => tabRefs.current.get(nextTab)?.focus())
  }

  const configureIfNeeded =

  async () => {
    if (access?.access_control) return
    const response = await api.put(resourceUrl(resourceType, resourceId), {})
    setAccess(response.data)
  }

  const addPerson =

  async event => {
    event.preventDefault()
    if (!principalId.trim()) return
    setBusy(true)
    setError('')
    try {
      await configureIfNeeded()
      await api.post(resourceUrl(resourceType, resourceId, '/grants'), {
        principal_type: principalType,
        principal_id: principalId.trim(),
        role: grantRole,
      })
      setPrincipalId('')
      setNotice('Access added')
      await load()
    } catch (grantError) {
      setError(message(grantError, 'Access could not be added'))
    } finally {
      setBusy(false)
    }
  }

  const removeGrant =

  async grantId => {
    if (!window.confirm('Remove this person or group’s access?')) return
    setBusy(true)
    setError('')
    try {
      await api.delete(resourceUrl(resourceType, resourceId, `/grants/${encodeURIComponent(grantId)}`))
      setNotice('Access removed')
      await load()
    } catch (grantError) {
      setError(message(grantError, 'Access could not be removed'))
    } finally {
      setBusy(false)
    }
  }

  const createLink =

  async event => {
    event.preventDefault()
    setBusy(true)
    setError('')
    try {
      await configureIfNeeded()
      const response = await api.post(resourceUrl(resourceType, resourceId, '/share-links'), {
        role: linkRole,
        allow_download: allowDownload,
        expires_at: expiresAt ? Math.floor(new Date(expiresAt).getTime() / 1000) : null,
        password,
      })
      const created = response.data?.share_link
      setNewLink(created)
      setPassword('')
      setNotice('Share link created')
      await load()
    } catch (shareError) {
      setError(message(shareError, 'Share link could not be created'))
    } finally {
      setBusy(false)
    }
  }

  const revokeLink =

  async shareId => {
    if (!window.confirm('Revoke this share link? Anyone using it will lose access.')) return
    setBusy(true)
    setError('')
    try {
      await api.delete(`/simul/archive/share-links/${encodeURIComponent(shareId)}`)
      setNotice('Share link revoked')
      await load()
    } catch (shareError) {
      setError(message(shareError, 'Share link could not be revoked'))
    } finally {
      setBusy(false)
    }
  }

  const copyText =

  async (value, success) => {
    try {
      await navigator.clipboard.writeText(value)
      setNotice(success)
    } catch (copyError) {
      setError('Copy failed. Select and copy the link manually.')
    }
  }

  const postComment =

  async event => {
    event.preventDefault()
    if (!commentBody.trim()) return
    setBusy(true)
    setError('')
    try {
      await api.post(resourceUrl(resourceType, resourceId, '/comments'), {
        body: commentBody.trim(),
        parent_comment_id: replyTo?.comment_id || null,
      })
      setCommentBody('')
      setReplyTo(null)
      setNotice(replyTo ? 'Reply posted' : 'Comment posted')
      await load()
    } catch (commentError) {
      setError(message(commentError, 'Comment could not be posted'))
    } finally {
      setBusy(false)
    }
  }

  const toggleResolved =

  async comment => {
    if (busy) return
    setBusy(true)
    setError('')
    try {
      await api.post(`/simul/archive/comments/${encodeURIComponent(comment.comment_id)}/resolve`, {
        resolved: !comment.resolved_at,
      })
      await load()
    } catch (commentError) {
      setError(message(commentError, 'Comment status could not be changed'))
    } finally {
      setBusy(false)
    }
  }

  const resolveRequest =

  async (requestId, approve) => {
    if (busy) return
    setBusy(true)
    setError('')
    try {
      await api.post(`/simul/archive/access-requests/${encodeURIComponent(requestId)}/resolve`, { approve })
      setNotice(approve ? 'Request approved' : 'Request declined')
      await load()
    } catch (requestError) {
      setError(message(requestError, 'Request could not be resolved'))
    } finally {
      setBusy(false)
    }
  }

  const grants = (access?.access_control?.grants || []).filter(grant => !grant.revoked_at)

  return (
    <section
      ref={panelRef}
      className="document-share-panel"
      aria-label={`Share ${resourceName}`}
      aria-busy={busy || undefined}
      tabIndex="-1"
      onKeyDown={event => { if (event.key === 'Escape' && onClose) onClose() }}>
      <header className="document-share-panel__header">
        <div>
          <div className="document-share-panel__eyebrow">Share and collaborate</div>
          <h2>{resourceName}</h2>
          <div className="document-share-panel__role" title="Your effective access">
            {roleLabel(access?.decision?.role || 'No access')}
          </div>
        </div>
        {onClose && <button type="button" className="document-share-panel__close" onClick={onClose} aria-label="Close sharing panel">×</button>}
      </header>

      <div className="document-share-panel__tabs" role="tablist" aria-label="Sharing sections" onKeyDown={selectAdjacentTab}>
        {tabs.map(([value, label]) => (
          <button
            key={value}
            ref={node => { if (node) tabRefs.current.set(value, node); else tabRefs.current.delete(value) }}
            id={`document-share-tab-${value}`}
            type="button"
            role="tab"
            tabIndex={tab === value ? 0 : -1}
            aria-selected={tab === value}
            aria-controls={`document-share-panel-${value}`}
            onClick={() => setTab(value)}>{label}</button>
        ))}
      </div>

      {error && <div className="document-share-panel__message document-share-panel__message--error" role="alert">{error}</div>}
      {notice && <div className="document-share-panel__message" role="status">{notice}</div>}

      <div
        className="document-share-panel__body"
        id={`document-share-panel-${tab}`}
        role="tabpanel"
        aria-labelledby={`document-share-tab-${tab}`}>
        {directVersionUrl && (
          <div className="document-share-panel__direct-link">
            <span><strong>Direct version link</strong><small>For people who already have access</small></span>
            <button type="button" onClick={() => copyText(directVersionUrl, 'Direct link copied')}>Copy</button>
          </div>
        )}
        {tab === 'people' && (
          <div>
            {access?.access_control?.owner_id && (
              <div className="document-share-panel__person">
                <span className="document-share-panel__avatar" aria-hidden="true">O</span>
                <span><strong>{access.access_control.owner_id}</strong><small>Owner</small></span>
              </div>
            )}
            {grants.map(grant => (
              <div className="document-share-panel__person" key={grant.grant_id}>
                <span className="document-share-panel__avatar" aria-hidden="true">{grant.principal_type === 'group' ? 'G' : 'U'}</span>
                <span><strong>{grant.principal_id}</strong><small>{roleLabel(grant.role)} · {grant.principal_type}</small></span>
                {canManage && <button type="button" disabled={busy} onClick={() => removeGrant(grant.grant_id)} aria-label={`Remove ${grant.principal_id}`}>Remove</button>}
              </div>
            ))}
            {!access?.access_control && <p className="document-share-panel__empty">Access has not been restricted. Authenticated firm users can edit this item.</p>}

            {canManage && (
              <form className="document-share-panel__form" onSubmit={addPerson}>
                <h3>Add access</h3>
                <div className="document-share-panel__row">
                  <select aria-label="Principal type" value={principalType} onChange={event => setPrincipalType(event.target.value)}>
                    <option value="user">User</option><option value="group">Group</option>
                  </select>
                  <input aria-label="User or group ID" value={principalId} onChange={event => setPrincipalId(event.target.value)} placeholder="User or group ID" />
                  <select aria-label="Access role" value={grantRole} onChange={event => setGrantRole(event.target.value)}>
                    {ROLES.map(role => <option key={role} value={role}>{roleLabel(role)}</option>)}
                  </select>
                  <button type="submit" disabled={busy || !principalId.trim()}>Add</button>
                </div>
              </form>
            )}

            {canManage && requests.filter(item => item.status === 'pending').length > 0 && <h3>Access requests</h3>}
            {canManage && requests.filter(item => item.status === 'pending').map(item => (
              <div className="document-share-panel__request" key={item.request_id}>
                <span><strong>{item.requester_id}</strong><small>Requests {roleLabel(item.requested_role)} access</small></span>
                <button type="button" disabled={busy} onClick={() => resolveRequest(item.request_id, true)}>Approve</button>
                <button type="button" disabled={busy} onClick={() => resolveRequest(item.request_id, false)}>Decline</button>
              </div>
            ))}
          </div>
        )}

        {tab === 'links' && (
          <div>
            {newLink?.token && (
              <div className="document-share-panel__new-link">
                <strong>Copy this link now</strong>
                <input readOnly aria-label="New share link" value={`${window.location.origin}${newLink.url}`} />
                <button type="button" onClick={() => copyText(`${window.location.origin}${newLink.url}`, 'Share link copied')}>Copy link</button>
              </div>
            )}
            {links.map(link => (
              <div className="document-share-panel__link" key={link.share_id}>
                <span><strong>{roleLabel(link.role)} link</strong><small>{link.revoked_at ? 'Revoked' : link.expires_at ? `Expires ${formatDate(link.expires_at)}` : 'No expiration'}{link.allow_download ? ' · Download allowed' : ''}{link.password_required ? ' · Password' : ''}</small></span>
                {!link.revoked_at && <button type="button" disabled={busy} onClick={() => revokeLink(link.share_id)}>Revoke</button>}
              </div>
            ))}
            {canManage && (
              <form className="document-share-panel__form" onSubmit={createLink}>
                <h3>Create an external link</h3>
                <div className="document-share-panel__row">
                  <select aria-label="Link role" value={linkRole} onChange={event => setLinkRole(event.target.value)}>
                    <option value="viewer">Viewer</option><option value="commenter">Commenter</option>
                  </select>
                  <input aria-label="Link expiration" type="datetime-local" value={expiresAt} onChange={event => setExpiresAt(event.target.value)} />
                  <input aria-label="Link password" type="password" value={password} onChange={event => setPassword(event.target.value)} placeholder="Password (optional)" />
                </div>
                <label className="document-share-panel__check"><input type="checkbox" checked={allowDownload} onChange={event => setAllowDownload(event.target.checked)} /> Allow downloads</label>
                <button type="submit" disabled={busy}>Create link</button>
              </form>
            )}
          </div>
        )}

        {tab === 'comments' && (
          <div>
            {comments.length === 0 && <p className="document-share-panel__empty">No comments yet.</p>}
            {comments.map(comment => (
              <article className={`document-share-panel__comment${comment.resolved_at ? ' is-resolved' : ''}`} key={comment.comment_id}>
                <header><strong>{comment.author_type === 'ai' ? 'AI Proofreader · AI-generated' : comment.author_id}</strong><time>{formatDate(comment.created_at)}</time></header>
                {comment.source_revision_id && <small>Reviewed version: {comment.source_revision_id}</small>}
                {comment.parent_comment_id && <small>Reply</small>}
                <p>{comment.body}</p>
                <footer>
                  <button type="button" onClick={() => setReplyTo(comment)}>Reply</button>
                  <button type="button" disabled={busy} onClick={() => toggleResolved(comment)}>{comment.resolved_at ? 'Reopen' : 'Resolve'}</button>
                </footer>
              </article>
            ))}
            {canComment && (
              <form className="document-share-panel__form" onSubmit={postComment}>
                {replyTo && <div className="document-share-panel__replying">Replying to {replyTo.author_id}<button type="button" onClick={() => setReplyTo(null)} aria-label="Cancel reply">×</button></div>}
                <textarea aria-label="Comment" value={commentBody} onChange={event => setCommentBody(event.target.value)} placeholder="Add a comment. Mention a user with @[user ID]." />
                <button type="submit" disabled={busy || !commentBody.trim()}>{replyTo ? 'Post reply' : 'Post comment'}</button>
              </form>
            )}
          </div>
        )}

        {tab === 'activity' && (
          <ol className="document-share-panel__activity">
            {events.map(event => <li key={event.event_id}><strong>{event.event_type.replaceAll('_', ' ')}</strong><span>{event.actor_id} · {formatDate(event.created_at)}</span></li>)}
            {events.length === 0 && <li>No access activity yet.</li>}
          </ol>
        )}
      </div>
    </section>
  )
}
